"use strict";
/**
 * basic localization functions
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.onPreferredLocalesChange = onPreferredLocalesChange;
exports.getPreferredLocales = getPreferredLocales;
exports.getSupportedLocales = getSupportedLocales;
exports.resetPreferredLocales = resetPreferredLocales;
exports.localizeText = localizeText;
exports.setPreferredLocales = setPreferredLocales;
exports.validateLocalizableText = validateLocalizableText;
const errors_ts_1 = require("./errors.js");
const supportedLocales = ['en', 'fr', 'es'];
Object.freeze(supportedLocales);
let preferredLocales = [...supportedLocales];
const localesListeners = new Set();
/**
 * Subscribe to preferred-locale changes. Returns an unsubscribe function.
 *
 * Why this exists: `localizeText()` reads module-level state at call time, so a
 * consumer that already rendered a string has no way to learn the locale moved
 * underneath it. Without a notification the caller only re-localizes on a full
 * reload — which was `BUGS.md` B-2026-07-10-1 (hds-webapp's live language switch
 * left data-model / method-spec strings in the old language until reload).
 *
 * Listeners fire only when the effective locale ORDER actually changes, so a
 * redundant `setPreferredLocales` with the same result does not churn consumers.
 */
function onPreferredLocalesChange(listener) {
    localesListeners.add(listener);
    return () => { localesListeners.delete(listener); };
}
/**
 * get the current preferred locales
 */
function getPreferredLocales() {
    return [...preferredLocales];
}
/**
 * get the current supported locales
 */
function getSupportedLocales() {
    return [...supportedLocales];
}
/**
 * reset prefferedLocalesTo Original state
 */
function resetPreferredLocales() {
    setPreferredLocales([...supportedLocales]);
}
function localizeText(textItem) {
    if (textItem == null)
        return null;
    const en = textItem.en;
    if (en == null)
        throw new errors_ts_1.HDSLibError('textItems must have an english translation', { textItem });
    const texts = textItem;
    for (const l of preferredLocales) {
        const v = texts[l];
        if (v != null)
            return v;
    }
    return en;
}
/**
 * Change prefferedLocal order
 */
function setPreferredLocales(arrayOfLocals) {
    if (!Array.isArray(arrayOfLocals)) {
        throw new errors_ts_1.HDSLibError('setPreferredLocales takes an array of language codes');
    }
    const unsupportedLocales = arrayOfLocals.filter(l => (supportedLocales.indexOf(l) < 0));
    if (unsupportedLocales.length > 0) {
        throw new errors_ts_1.HDSLibError(`locales "${unsupportedLocales.join(', ')}" are not supported`, arrayOfLocals);
    }
    const next = [...new Set([...arrayOfLocals, ...preferredLocales])];
    // Compare BEFORE assigning so listeners fire only on a real change.
    const changed = next.length !== preferredLocales.length || next.some((l, i) => l !== preferredLocales[i]);
    preferredLocales = next;
    if (!changed)
        return;
    const snapshot = [...preferredLocales];
    for (const listener of [...localesListeners]) {
        // One bad listener must not break a locale change for every other consumer.
        try {
            listener(snapshot);
        }
        catch (e) {
            console.error('onPreferredLocalesChange listener threw', e);
        }
    }
}
/**
 * throw errors if an item is not of type localizableText
 */
function validateLocalizableText(key, toTest) {
    if (toTest.en == null || typeof toTest.en !== 'string')
        throw new errors_ts_1.HDSLibError(`Missing or invalid localizable text for ${key}`, { [key]: toTest });
    for (const optionalLang of supportedLocales) {
        if (optionalLang === 'en')
            continue;
        if (toTest[optionalLang] != null && typeof toTest[optionalLang] !== 'string')
            throw new errors_ts_1.HDSLibError(`Missing or invalid localizable text for ${key} languagecode: ${optionalLang}`, { [key]: toTest, languageCode: optionalLang });
    }
    return toTest;
}
